<template>
  <a-breadcrumb style="margin: 16px 0">
    <a-breadcrumb-item>南向统一互联</a-breadcrumb-item>
    <a-breadcrumb-item>共建榜单</a-breadcrumb-item>
  </a-breadcrumb>
  <southbound-overview />
  <div class="table-container">
    <div class="table-title">
      统一互联代码共建榜单
    </div>
    <div style="text-align: left; margin: 16px 0;">
      <span class="caption-and-select-container">
        <span class="caption">项目</span>
        <a-select v-model:value="project" style="width: 120px;">
          <a-select-option v-for="opt in projectOptions" :key="opt.key" :value="opt.value">
            {{ opt.label }}
          </a-select-option>
        </a-select>
      </span>
      <span class="caption-and-select-container">
        <span class="caption">分支</span>
        <a-select v-model:value="branch" style="width: 240px;">
          <a-select-option v-for="opt in branchOptions" :key="opt.key" :value="opt.value">
            {{ opt.label }}
          </a-select-option>
        </a-select>
      </span>
      <a-checkbox v-model:checked="specialRule">应用特殊规则</a-checkbox>
      <a-tooltip title="特殊规则：过滤掉自动化提交的PR，仅统计人工审核的代码贡献。 包括排除机器人提交、自动格式化等非实质性代码修改。">
        <info-circle-outlined style="color: #9ca3af;" />
      </a-tooltip>
    </div>
    <a-spin :spinning="loading">
      <a-table :columns="columns" :data-source="filteredTableData" :pagination="{
        showTotal: (total, range) => `第 ${range[0]}—${range[1]} 条，共 ${total} 条`,
        showSizeChanger: true
      }" :rowKey="record => record.key">
        <template #bodyCell="{ column, record }">
          <template v-if="column.dataIndex === 'rank'">
            <span v-if="record.rank <= 3" :class="['rank-badge', `rank-badge-${record.rank}`]">{{ record.rank }}</span>
            <span v-else>{{ record.rank }}</span>
          </template>
        </template>
      </a-table>
    </a-spin>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import axios from 'axios';
import { notification } from 'ant-design-vue';
import { InfoCircleOutlined } from '@ant-design/icons-vue';
import SouthboundOverview from './SouthboundOverview.vue';

const projectOptions = [
  { key: 'unified', label: '统一互联', value: 'unified' },
];
const branchOptions = [
  { key: 'all', label: '全部', value: '' },
  { key: 'master', label: 'master', value: 'master' },
  { key: 'OpenHarmony-5.0.0-Release', label: 'OpenHarmony-5.0.0-Release', value: 'OpenHarmony-5.0.0-Release' },
  { key: 'OpenHarmony-5.1.0-Release', label: 'OpenHarmony-5.1.0-Release', value: 'OpenHarmony-5.1.0-Release' },
];


const project = ref('unified');
const branch = ref('');
const specialRule = ref(false);
const loading = ref(false);
const cache = ref({
  0: null,
  1: null
});

const columns = [
  { title: '排名', dataIndex: 'rank', key: 'rank', width: 80, align: 'center' },
  { title: '伙伴', dataIndex: 'company', key: 'company', width: 160 },
  { title: '分支', dataIndex: 'branch', key: 'branch', width: 220 },
  { title: 'PR数量', dataIndex: 'prCount', key: 'prCount', width: 120 },
  {
    title: 'PR新增代码量',
    dataIndex: 'addedLines',
    key: 'addedLines',
    width: 160,
    sorter: (a, b) => a.addedLines - b.addedLines,
    showSorterTooltip: false
  },
  {
    title: 'PR删除代码量',
    dataIndex: 'removedLines',
    key: 'removedLines',
    width: 160,
    sorter: (a, b) => a.removedLines - b.removedLines,
    showSorterTooltip: false
  },
  {
    title: '修改代码总量（增+删）',
    dataIndex: 'modifyLines',
    key: 'modifyLines',
    width: 200,
    sorter: (a, b) => a.modifyLines - b.modifyLines,
    showSorterTooltip: false
  },
  {
    title: '修改量占比',
    dataIndex: 'rate',
    key: 'rate',
    width: 140,
    sorter: (a, b) => a.rate - b.rate,
    showSorterTooltip: false,
    customRender: ({ text }) => `${(Number(text) * 100).toFixed(2)}%`
  }
];

const tableData = ref([]);

// 分支改变时刷新排名
const filteredTableData = computed(() => {
  let rows = !branch.value ? tableData.value : tableData.value.filter((row) => row.branch === branch.value);
  // 重新赋 rank
  return rows.map((row, idx) => ({ ...row, rank: idx + 1 }));
});

const mapToRows = (items) => items.map((item, index) => ({
  key: `${item.company}-${item.branch}-${index}`,
  rank: index + 1,
  company: item.company,
  branch: item.branch,
  prCount: item.prCount,
  addedLines: item.addedLines,
  removedLines: item.removedLines,
  modifyLines: item.modifyLines,
  rate: item.rate
}));

const fetchBoardData = async (state) => {
  loading.value = true;
  try {
    const res = await axios.get('/metric/getBoardData', {
      params: { state }
    });
    if (res.data?.code !== 200) {
      throw new Error(res.data?.desc || '请求失败');
    }
    const rows = mapToRows(res.data?.data || []);
    cache.value[state] = rows;
    tableData.value = rows;
  } catch (error) {
    notification.error({
      message: '数据加载失败',
      description: error?.message || error
    });
  } finally {
    loading.value = false;
  }
};

watch(
  specialRule,
  (val) => {
    const state = val ? 1 : 0;
    if (cache.value[state]) {
      tableData.value = cache.value[state];
      return;
    }
    fetchBoardData(state);
  },
  { immediate: true }
);

</script>

<style scoped>
.table-container {
  padding: 25px;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  background-color: #ffffff;
}

.table-title {
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #111111;
}

.caption-and-select-container {
  text-align: left;
  margin-right: 24px;
}

.caption {
  margin-right: 8px;
  font-weight: 500;
  font-size: 14px;
  color: #333333;
}

.rank-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  font-weight: 700;
  color: #ffffff;
  font-size: 13px;
  background: #9ca3af;
}

.rank-badge-1 {
  background: #f59e0b;
}

.rank-badge-2 {
  background: #9ca3af;
}

.rank-badge-3 {
  background: #b45309;
}
</style>